import React, {Component, Fragment} from 'react'
import {
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Input,
    InputGroup,
    InputGroupAddon,
    Button,
} from 'reactstrap'
import autobind from 'auto-bind'
import moment from 'moment'
import PropTypes from 'prop-types'

import HourlyTargetActualChart from './HourlyTargetActualChart'
import Subscriber from 'SDK/subscriber'
import * as API from 'SDK/api'
import {secondsToHHMMSS, tileColor} from 'SDK/helpers'

export default class StaticHourlyActualTile extends Component {
    deprecated = true
    propComponents = [
        {
            prop: 'name',
            component: 'GenericWidgetName',
        },
        {
            prop: 'deviceId',
            component: 'AssetPicker',
        },
    ]
    requiredOutputs = [
        'Part Count',
        'In-Cycle',
        'Downtime',
        'Static Hourly Target',
    ]
    showBorder = false
    id = 'StaticHourlyActualTile'
    static propTypes = {
        deviceId: PropTypes.string,
        name: PropTypes.string,
    }
    constructor(props) {
        super(props)
        autobind(this)

        this.subscriber = new Subscriber()

        this.state = {
            name: '',
            live: {
                name: '...',
                timestamp: null,
                color: '',
                elapsed: '0:00',
            },
            actual: 0,
            target: 0,
            partNumber: 'No Scheduled Production',
            left: 0,
            progress: 0,
            hoursRemaining: '',
            activeBreak: false,
        }
    }

    async fetchDeviceName() {
        let assets = await API.get('devices')
        if (!assets) {
            return alert('There was an error fetching data')
        } else {
            this.setState({
                name: assets.find((a) => a.deviceId === this.props.deviceId)
                    .name,
            })
        }
    }

    async fetchLiveData() {
        let live = await API.post('live', {
            deviceId: this.props.deviceId,
        })
        if (!live) {
            return alert('There was an error fetching data')
        } else if (live[this.props.deviceId].activeStates.length) {
            if (live[this.props.deviceId].activeShiftBreaks.length) {
                this.setState({
                    activeBreak:
                        live[this.props.deviceId].activeShiftBreaks[0].name,
                })
            } else {
                this.setState({activeBreak: false})
            }
            live[this.props.deviceId].activeStates = live[
                this.props.deviceId
            ].activeStates.filter(
                (state) =>
                    state.name === 'Downtime' || state.name === 'In-Cycle'
            )
            live = live[this.props.deviceId].activeStates[0]
            if (live.name === 'Downtime') {
                const downtimeReason = await API.post(
                    'historical/raw',
                    {
                        query: {
                            deviceId: this.props.deviceId,
                            name: 'Downtime Reason',
                            timeEnd: null,
                        },
                    },
                    2
                )

                if (downtimeReason.length) {
                    live.name = downtimeReason[0].value
                }
            }
            live.color = tileColor(live.name)
            live.elapsed = secondsToHHMMSS(
                moment().diff(moment(live.timestamp), 'seconds')
            )
            this.setState({live})
        }
    }

    updateElapsed() {
        if (this.state.live.timestamp === null) return
        let live = {...this.state.live}
        live.elapsed = secondsToHHMMSS(
            moment().diff(moment(live.timestamp), 'seconds')
        )
        this.setState({live})
    }

    async fetchActualThisHour() {
        let {data} = await API.post('historical/aggregate2', {
            timeStart: moment().startOf('hour').toISOString(),
            timeEnd: moment().toISOString(),
            state: ['Part Count'],
            deviceId: [this.props.deviceId],
            groupByTimeUnit: 'total',
            logic: 'count',
        })

        let parts = data.devices[this.props.deviceId]['Part Count'][0]
        if (parts.constructor === Object) parts = parts.count

        this.setState({
            actual: parts,
        })
    }

    async fetchTargetThisHour() {
        const data = await API.post(
            'historical/raw',
            {
                query: {
                    deviceId: this.props.deviceId,
                    name: {$in: ['Hourly Target', 'Scheduled Target']},
                    timeEnd: null,
                },
            },
            2
        )
        if (data && data.length) {
            this.setState({
                target: data[0].metaData[0].value,
            })
        }
    }

    async fetchPartNumber() {
        const data = await API.post(
            'historical/raw',
            {
                query: {
                    deviceId: this.props.deviceId,
                    name: 'Part Number',
                    timeEnd: null,
                },
            },
            2
        )
        if (data && data.length) {
            this.setState({
                partNumber: data[0].metaData[0].value,
            })
        }
    }

    async fetchProgress() {
        const target = await API.post(
            'historical/raw',
            {
                query: {
                    deviceId: this.props.deviceId,
                    name: 'Target Parts',
                    timeEnd: null,
                },
            },
            2
        )
        if (target && target.length) {
            let {data} = await API.post('historical/aggregate2', {
                timeStart: moment(target[0].timeStart).toISOString(),
                timeEnd: moment().toISOString(),
                state: ['Part Count'],
                deviceId: [this.props.deviceId],
                groupByTimeUnit: 'total',
                logic: 'count',
            })

            let actual = data.devices[this.props.deviceId]['Part Count'][0]
            if (actual.constructor === Object) actual = actual.count

            let left = target[0].metaData[0].value - actual,
                progress = parseInt((actual / left) * 100),
                timeElapsedThisHour = moment().diff(moment().startOf('hour')),
                cycleTime = timeElapsedThisHour / this.state.actual,
                hoursRemaining = (left * cycleTime) / (60 * 60 * 1000)

            if (isNaN(hoursRemaining)) {
                hoursRemaining = ''
            } else {
                hoursRemaining =
                    '(~' + Math.round((hoursRemaining / 100) * 100) + ' hours)'
            }

            this.setState({
                left,
                progress,
                hoursRemaining,
            })
        }
    }

    toggleEditTarget() {
        this.setState({
            editingTarget: !this.state.editingTarget,
        })
    }

    async saveTarget() {
        await API.post(
            'tablets/data',
            [
                {
                    nodeId: this.props.deviceId,
                    name: 'Static Hourly Target',
                    value: parseInt(this.state.target),
                    timestamp: moment().valueOf(),
                },
            ],
            2
        )
        this.toggleEditTarget()
    }

    componentWillMount() {
        this.fetchDeviceName()
        this.subscriber.add(this.fetchLiveData, 5000, 'fetchLiveData')
        this.subscriber.add(this.updateElapsed, 750, 'updateElapsed')
        this.subscriber.add(
            this.fetchActualThisHour,
            30 * 1000,
            'fetchActualThisHour'
        )
        this.subscriber.add(
            this.fetchTargetThisHour,
            60 * 1000,
            'fetchTargetThisHour'
        )
    }

    componentWillUnmount() {
        this.subscriber.removeAll()
    }

    render() {
        const activeState = this.state.live

        return (
            <Card style={{color: '#fff', backgroundColor: activeState.color}}>
                <CardHeader
                    style={{
                        cursor: 'pointer',
                        margin: 0,
                        padding: '5px',
                        backgroundColor: 'rgba(47, 50, 59,0.2)',
                    }}>
                    <Row>
                        <Col>
                            <strong>{this.state.name}</strong>
                        </Col>
                        <Col style={{textAlign: 'right'}}>
                            {this.state.live.elapsed}
                        </Col>
                    </Row>
                </CardHeader>
                <CardBody style={{padding: 0}}>
                    <Row style={{margin: 0}}>
                        <Col xs="6">
                            <Row>
                                <Col className="text-center">
                                    <h1>
                                        {this.state.live.name.length > 15
                                            ? this.state.live.name.substring(
                                                  0,
                                                  12
                                              ) + '...'
                                            : this.state.live.name}
                                    </h1>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="text-center">
                                    <p>Hourly Actual</p>
                                    <h1>{this.state.actual}</h1>
                                </Col>
                                <Col className="text-center">
                                    <p>Hourly Target</p>
                                    {this.state.editingTarget ? (
                                        <InputGroup className="mb-3">
                                            <Input
                                                type="number"
                                                value={this.state.target}
                                                style={{
                                                    borderColor:
                                                        this.state.color,
                                                    borderTop: 'transparent',
                                                    borderRight: 'transparent',
                                                    borderLeft: 'transparent',
                                                    backgroundColor:
                                                        'transparent',
                                                }}
                                                onChange={(e) => {
                                                    if (e.target.value < 0)
                                                        return
                                                    this.setState({
                                                        target: e.target.value,
                                                    })
                                                }}
                                            />
                                            <InputGroupAddon addonType="append">
                                                <Button
                                                    outline
                                                    color="primary"
                                                    size="sm"
                                                    onClick={this.saveTarget}>
                                                    Save
                                                </Button>
                                            </InputGroupAddon>
                                        </InputGroup>
                                    ) : (
                                        <h1
                                            style={{cursor: 'pointer'}}
                                            onClick={this.toggleEditTarget}>
                                            {this.state.target}
                                        </h1>
                                    )}
                                </Col>
                            </Row>
                        </Col>
                        <Col
                            xs="6"
                            style={{backgroundColor: 'rgba(68, 70, 79, 0.5)'}}>
                            <HourlyTargetActualChart
                                deviceId={this.props.deviceId}
                                target={this.state.target}
                                color={this.state.live.color}
                            />
                        </Col>
                    </Row>
                </CardBody>
                {this.state.activeBreak ? (
                    <CardFooter>
                        <p style={{textAlign: 'center'}}>
                            {this.state.activeBreak}
                        </p>
                    </CardFooter>
                ) : null}
            </Card>
        )
    }
}
